import { FormEvent, useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, Mail, Route, ShieldCheck } from 'lucide-react';
import type { IntakeIntent, IntakeRouteDetail } from '../lib/intakeRouting';

type SourceId = 'findable' | 'viability' | 'macellum_direct' | 'agency' | 'manual' | 'unknown';

type FormState = {
  sourceId: SourceId;
  sourceUrl: string;
  scoreBand: string;
  productHint: string;
  intent: IntakeIntent;
  businessName: string;
  websiteUrl: string;
  location: string;
  sector: string;
  contactName: string;
  contactEmail: string;
  noPresence: boolean;
  stalePresence: boolean;
  expensivePresence: boolean;
  aeoGap: boolean;
  proofStreamNeeded: boolean;
  agencyPartner: boolean;
  notes: string;
  contactPermission: boolean;
  processBusinessInfo: boolean;
  privacyAcknowledged: boolean;
};

type BooleanField =
  | 'noPresence'
  | 'stalePresence'
  | 'expensivePresence'
  | 'aeoGap'
  | 'proofStreamNeeded'
  | 'agencyPartner';

const sourceOptions: SourceId[] = ['macellum_direct', 'findable', 'viability', 'agency', 'manual', 'unknown'];
const intentOptions: Array<{ value: IntakeIntent; label: string; hint: string }> = [
  { value: 'm2m', label: 'Message-to-Market', hint: 'Proof-to-market system' },
  { value: 'aeo', label: 'AEO Visibility Repair', hint: 'Evidence-layer repair' },
  { value: 'agency', label: 'Agency path', hint: 'White-label fulfilment' },
  { value: 'unsure', label: 'Manual review', hint: 'Route after review' },
];

// Accept the contract's viabilAIty source aliases and normalise to the canonical value.
function normaliseSource(raw: string | null): SourceId | null {
  if (!raw) {
    return null;
  }
  const value = raw.toLowerCase();
  // Contract §7.1 aliases: viability | viabilaity | viabilAIty -> canonical "viability".
  if (value === 'viability' || value === 'viabilaity') {
    return 'viability';
  }
  return sourceOptions.includes(value as SourceId) ? (value as SourceId) : null;
}

function getInitialState(): FormState {
  const params = new URLSearchParams(window.location.search);
  const sourceParam = params.get('source_service') || params.get('source');
  const intentParam = params.get('intent') as IntakeIntent | null;
  const productHint = params.get('product_hint') || '';

  const sourceId = normaliseSource(sourceParam) ?? 'macellum_direct';
  const intent: IntakeIntent = intentOptions.some((option) => option.value === intentParam)
    ? (intentParam as IntakeIntent)
    : 'm2m';

  return {
    sourceId,
    sourceUrl: params.get('source_url') || params.get('scan_url') || '',
    scoreBand: params.get('score_band') || params.get('findable_score') || '',
    productHint,
    intent,
    businessName: '',
    websiteUrl: '',
    location: '',
    sector: '',
    contactName: '',
    contactEmail: '',
    noPresence: productHint === 'm2m_t2',
    stalePresence: false,
    expensivePresence: false,
    aeoGap: intent === 'aeo',
    proofStreamNeeded: intent === 'm2m',
    agencyPartner: intent === 'agency',
    notes: '',
    contactPermission: false,
    processBusinessInfo: false,
    privacyAcknowledged: false,
  };
}

function buildSummary(state: FormState) {
  const states = [
    state.noPresence ? 'no_presence' : '',
    state.stalePresence ? 'stale_presence' : '',
    state.expensivePresence ? 'expensive_presence' : '',
    state.aeoGap ? 'aeo_gap' : '',
    state.proofStreamNeeded ? 'proof_stream_needed' : '',
    state.agencyPartner ? 'agency_partner' : '',
  ].filter(Boolean);

  return [
    'Macellum v0.2 routed intake',
    '',
    `source_id: ${state.sourceId}`,
    `source_url: ${state.sourceUrl || 'not supplied'}`,
    `score_band: ${state.scoreBand || 'not supplied'}`,
    `intent: ${state.intent}`,
    `product_hint: ${state.productHint || 'not supplied'}`,
    '',
    'business:',
    `  name: ${state.businessName || 'not supplied'}`,
    `  website_url: ${state.websiteUrl || 'not supplied'}`,
    `  location: ${state.location || 'not supplied'}`,
    `  sector: ${state.sector || 'not supplied'}`,
    '',
    'contact:',
    `  name: ${state.contactName || 'not supplied'}`,
    `  email: ${state.contactEmail || 'not supplied'}`,
    '',
    `prospect_state: ${states.length ? states.join(', ') : 'unclear'}`,
    '',
    'consent:',
    `  contact_permission: ${state.contactPermission}`,
    `  process_business_info: ${state.processBusinessInfo}`,
    `  later_steps_acknowledged: ${state.privacyAcknowledged}`,
    '',
    'notes:',
    state.notes || 'not supplied',
    '',
    'approval_state: draft_only_preparation (publication requires separate approval).',
    'proof_upload_status: not accepted in public v0.2 intake; proof collection follows after contact and permission review.',
  ].join('\n');
}

export function IntakeRouter() {
  const [form, setForm] = useState<FormState>(() => getInitialState());
  const [summary, setSummary] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    function handleRoute(event: Event) {
      const detail = (event as CustomEvent<IntakeRouteDetail>).detail;
      setForm((current) => ({
        ...current,
        intent: detail.intent,
        productHint: detail.productHint || current.productHint,
        sourceId: (normaliseSource(detail.sourceId ?? null) as SourceId) || current.sourceId,
        aeoGap: detail.intent === 'aeo' ? true : current.aeoGap,
        proofStreamNeeded: detail.intent === 'm2m' ? true : current.proofStreamNeeded,
        agencyPartner: detail.intent === 'agency' ? true : current.agencyPartner,
      }));
    }

    window.addEventListener('macellum:intake-route', handleRoute);
    return () => window.removeEventListener('macellum:intake-route', handleRoute);
  }, []);

  const mailtoHref = useMemo(() => {
    if (!summary) {
      return '';
    }

    const subject = encodeURIComponent(`Macellum intake - ${form.businessName || form.intent}`);
    const body = encodeURIComponent(summary);
    return `mailto:hello@sidommedia.com?subject=${subject}&body=${body}`;
  }, [form.businessName, form.intent, summary]);

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function updateBooleanField(key: BooleanField, value: boolean) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!form.contactPermission || !form.processBusinessInfo || !form.privacyAcknowledged) {
      setError('Please confirm contact permission, business-information processing, and the later-steps acknowledgement before preparing the intake.');
      setSummary('');
      return;
    }

    setError('');
    setSummary(buildSummary(form));
  }

  const inputClass =
    'h-11 min-w-0 border border-white/15 bg-obsidian/60 px-3 text-chalk placeholder:text-ash/60 outline-none focus:border-cyan focus:ring-2 focus:ring-cyan/20';

  return (
    <section
      id="intake"
      className="border-y border-white/10 bg-obsidian py-24 text-chalk sm:py-28"
      data-aeo-section="intake-router"
    >
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <div className="min-w-0">
            <p className="text-sm font-black uppercase tracking-wide text-cyan">Routed intake</p>
            <h2 className="mt-4 text-4xl font-black leading-tight tracking-tight sm:text-5xl">
              Tell Macellum where the proof bottleneck is.
            </h2>
            <p className="mt-6 text-lg leading-8 text-ash">
              The public intake captures source, intent, business context, and consent. Proof uploads
              stay out of v0.2 — proof collection happens later, by email or operator handoff, after
              permission review.
            </p>

            <div className="mt-8 grid gap-4">
              <div className="flex gap-3 border-l-2 border-cyan bg-white/[0.03] px-4 py-4">
                <Route className="mt-1 size-5 shrink-0 text-cyan" aria-hidden="true" />
                <p className="text-sm leading-6 text-ash">
                  Source context can come from Findable, viabilAIty, direct traffic, agency partners,
                  or manual review — none are required to proceed.
                </p>
              </div>
              <div className="flex gap-3 border-l-2 border-gold bg-white/[0.03] px-4 py-4">
                <ShieldCheck className="mt-1 size-5 shrink-0 text-gold" aria-hidden="true" />
                <p className="text-sm leading-6 text-ash">
                  Submitting this form does not authorise publishing. Everything starts as
                  draft-only; publication needs separate approval after you review the draft.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="min-w-0 border border-white/12 bg-white/[0.03] p-5 sm:p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="grid min-w-0 gap-2 text-sm font-bold">
                Source
                <select
                  value={form.sourceId}
                  onChange={(event) => updateField('sourceId', event.target.value as SourceId)}
                  className={inputClass}
                >
                  {sourceOptions.map((source) => (
                    <option key={source} value={source} className="bg-obsidian text-chalk">
                      {source}
                    </option>
                  ))}
                </select>
              </label>

              <label className="grid min-w-0 gap-2 text-sm font-bold">
                Product path
                <select
                  value={form.intent}
                  onChange={(event) => updateField('intent', event.target.value as IntakeIntent)}
                  className={inputClass}
                >
                  {intentOptions.map((option) => (
                    <option key={option.value} value={option.value} className="bg-obsidian text-chalk">
                      {option.label} - {option.hint}
                    </option>
                  ))}
                </select>
              </label>

              <label className="grid min-w-0 gap-2 text-sm font-bold">
                Business name
                <input
                  value={form.businessName}
                  onChange={(event) => updateField('businessName', event.target.value)}
                  className={inputClass}
                  placeholder="Business trading name"
                />
              </label>

              <label className="grid min-w-0 gap-2 text-sm font-bold">
                Website URL
                <input
                  type="url"
                  value={form.websiteUrl}
                  onChange={(event) => updateField('websiteUrl', event.target.value)}
                  className={inputClass}
                  placeholder="https://example.com"
                />
              </label>

              <label className="grid min-w-0 gap-2 text-sm font-bold">
                Location
                <input
                  value={form.location}
                  onChange={(event) => updateField('location', event.target.value)}
                  className={inputClass}
                  placeholder="Town, county, or service area"
                />
              </label>

              <label className="grid min-w-0 gap-2 text-sm font-bold">
                Sector
                <input
                  value={form.sector}
                  onChange={(event) => updateField('sector', event.target.value)}
                  className={inputClass}
                  placeholder="Trade, local service, agency, founder"
                />
              </label>

              <label className="grid min-w-0 gap-2 text-sm font-bold">
                Contact name
                <input
                  value={form.contactName}
                  onChange={(event) => updateField('contactName', event.target.value)}
                  className={inputClass}
                  placeholder="Name"
                />
              </label>

              <label className="grid min-w-0 gap-2 text-sm font-bold">
                Contact email
                <input
                  type="email"
                  value={form.contactEmail}
                  onChange={(event) => updateField('contactEmail', event.target.value)}
                  className={inputClass}
                  placeholder="name@example.com"
                />
              </label>
            </div>

            <fieldset className="mt-6 border border-white/15 p-4">
              <legend className="px-2 text-sm font-black uppercase tracking-wide text-cyan">Current state</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                {([
                  ['noPresence', 'No website or meaningful presence'],
                  ['stalePresence', 'Site or socials are stale'],
                  ['expensivePresence', 'Updates are slow or expensive'],
                  ['aeoGap', 'AI visibility or entity gap'],
                  ['proofStreamNeeded', 'Recurring proof stream needed'],
                  ['agencyPartner', 'Agency or partner route'],
                ] as Array<[BooleanField, string]>).map(([key, label]) => (
                  <label key={key} className="flex min-h-11 items-center gap-3 border border-white/12 px-3 py-2 text-sm font-semibold">
                    <input
                      type="checkbox"
                      checked={form[key]}
                      onChange={(event) => updateBooleanField(key, event.target.checked)}
                      className="size-4 accent-cyan"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <label className="grid min-w-0 gap-2 text-sm font-bold">
                Source URL
                <input
                  type="url"
                  value={form.sourceUrl}
                  onChange={(event) => updateField('sourceUrl', event.target.value)}
                  className={inputClass}
                  placeholder="Optional public URL (e.g. Findable scan)"
                />
              </label>

              <label className="grid min-w-0 gap-2 text-sm font-bold">
                Source score band
                <input
                  value={form.scoreBand}
                  onChange={(event) => updateField('scoreBand', event.target.value)}
                  className={inputClass}
                  placeholder="low, medium, high, or unknown"
                />
              </label>
            </div>

            <label className="mt-6 grid min-w-0 gap-2 text-sm font-bold">
              Notes
              <textarea
                value={form.notes}
                onChange={(event) => updateField('notes', event.target.value)}
                className="min-h-28 min-w-0 resize-y border border-white/15 bg-obsidian/60 px-3 py-3 text-chalk placeholder:text-ash/60 outline-none focus:border-cyan focus:ring-2 focus:ring-cyan/20"
                placeholder="What should Macellum review first?"
              />
            </label>

            <div className="mt-6 grid gap-3">
              <label className="flex items-start gap-3 text-sm font-semibold leading-6">
                <input
                  type="checkbox"
                  checked={form.contactPermission}
                  onChange={(event) => updateField('contactPermission', event.target.checked)}
                  className="mt-1 size-4 accent-cyan"
                />
                Macellum may contact me about this enquiry.
              </label>
              <label className="flex items-start gap-3 text-sm font-semibold leading-6">
                <input
                  type="checkbox"
                  checked={form.processBusinessInfo}
                  onChange={(event) => updateField('processBusinessInfo', event.target.checked)}
                  className="mt-1 size-4 accent-cyan"
                />
                Macellum may process the business information I have entered above to scope my route.
              </label>
              <label className="flex items-start gap-3 text-sm font-semibold leading-6">
                <input
                  type="checkbox"
                  checked={form.privacyAcknowledged}
                  onChange={(event) => updateField('privacyAcknowledged', event.target.checked)}
                  className="mt-1 size-4 accent-cyan"
                />
                I understand proof uploads, client images, and publication each require a separate,
                later consent step.
              </label>
            </div>

            {error ? (
              <p className="mt-5 border border-pink/40 bg-pink/10 px-4 py-3 text-sm font-bold text-pink">{error}</p>
            ) : null}

            <button
              type="submit"
              className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 bg-cyan px-5 text-sm font-black uppercase tracking-wide text-obsidian transition hover:bg-violet hover:text-chalk"
            >
              <ClipboardCheck className="size-5" aria-hidden="true" />
              Prepare intake summary
            </button>

            {summary ? (
              <div className="mt-6 border border-white/12 bg-obsidian/60 p-4">
                <p className="text-sm font-black uppercase tracking-wide text-cyan">Prepared handoff</p>
                <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap text-xs leading-5 text-ash">{summary}</pre>
                <a
                  href={mailtoHref}
                  className="mt-4 inline-flex h-11 items-center justify-center gap-2 bg-violet px-4 text-sm font-black uppercase tracking-wide text-chalk transition hover:bg-cyan hover:text-obsidian"
                >
                  <Mail className="size-4" aria-hidden="true" />
                  Email Macellum intake
                </a>
              </div>
            ) : null}
          </form>
        </div>
      </div>
    </section>
  );
}
